/**
 * Sign Up Page — Multi-step: Registration → Verification → Checkout
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authApi } from '../api/auth';
import { stripeApi } from '../api/stripe';
import { COGNITO_CONFIG } from '../api/config';
import { startOAuth } from '../api/oauth';
const TIER_LABELS: Record<string, string> = {
  team: 'Pro Solo',
  professional: 'Pro Team',
};

const TIER_PRICES: Record<string, string> = {
  team: '$49/mo',
  professional: '$149/mo',
};

type Step = 'register' | 'verify' | 'checkout';

const SignUp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const tier = searchParams.get('tier') || '';
  const validTier = ['team', 'professional'].includes(tier);

  // Redirect to pricing if no valid tier selected
  useEffect(() => {
    if (!validTier) {
      window.location.href = 'https://mindmeld.dev/#pricing';
    }
  }, [validTier]);

  const oauthComplete = searchParams.get('oauth') === 'complete' && validTier;
  const [step, setStep] = useState<Step>(oauthComplete ? 'checkout' : 'register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [website, setWebsite] = useState(''); // honeypot — real users never see this

  const navigate = useNavigate();

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };
  const passwordValid = Object.values(passwordChecks).every(Boolean);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Honeypot — bots auto-fill this hidden field, real users never see it
    if (website) {
      // Silently pretend success so bots don't know they were caught
      setStep('verify');
      return;
    }

    if (!passwordValid) {
      setError('Password does not meet requirements');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await authApi.signUp(email, password);
      if (!result.success) {
        if (result.error?.includes('UsernameExistsException') || result.error?.includes('already exists')) {
          setError('An account with this email already exists. Try signing in instead.');
        } else {
          setError(result.error || 'Sign up failed');
        }
        return;
      }
      setStep('verify');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign up failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const confirmResult = await authApi.confirmSignUp(email, verificationCode);
      if (!confirmResult.success) {
        setError(confirmResult.error || 'Verification failed');
        return;
      }
      setStep('checkout');
      await handleCheckout();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckout = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      // Sign in to get auth token (don't use full authenticate — it loads user data which may fail for new accounts)
      const oauthSession = oauthComplete ? await authApi.getCurrentSession() : null;
      const signInResult = oauthComplete ? { success: oauthSession?.valid === true, error: 'OAuth session expired' } : await authApi.signIn(email, password);
      if (!signInResult.success) {
        console.error('[SignUp] Sign in failed:', signInResult.error);
        setError('Account created but sign-in failed. Please sign in manually and select a plan.');
        return;
      }

      if (validTier) {
        // Create Stripe checkout session with retry — PostConfirmation Lambda may not have
        // finished creating DB records yet (timing race between Cognito trigger and this call)
        let checkoutResult = null;
        for (let attempt = 1; attempt <= 3; attempt++) {
          checkoutResult = await stripeApi.createCheckoutSession(tier, 'contributing');
          if (checkoutResult.success && checkoutResult.data) {
            const data = checkoutResult.data as any;
            const checkoutUrl = data?.data?.Records?.[0]?.checkoutUrl || data?.url;
            if (checkoutUrl) {
              window.location.href = checkoutUrl;
              return;
            }
          }
          // If user not found (404), wait and retry — PostConfirmation may still be running
          if (attempt < 3 && checkoutResult.error?.includes('not found')) {
            console.log(`[SignUp] Checkout attempt ${attempt} failed (user not ready), retrying in 1s...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else {
            break;
          }
        }
        console.error('[SignUp] Checkout result:', JSON.stringify(checkoutResult));
        // Fallback: send to subscription page where they can complete checkout
        navigate('/subscriptions');
      } else {
        window.location.href = 'https://mindmeld.dev/#pricing';
      }
    } catch (err) {
      console.error('[SignUp] Checkout setup failed:', err);
      setError('Account created successfully but checkout setup failed. Please sign in and select a plan.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    const result = await authApi.resendCode(email);
    if (result.success) {
      setResendCooldown(60);
    } else {
      setError(result.error || 'Failed to resend code');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      {/* Authorized Use Banner */}
      <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2">
        <p className="text-center text-xs text-yellow-800">
          This system is for authorized use only. By creating an account, you agree to our{' '}
          <a href="https://mindmeld.dev/terms.html" className="underline hover:text-yellow-900" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="https://mindmeld.dev/privacy.html" className="underline hover:text-yellow-900" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>.
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h1 className="text-center text-4xl font-bold text-mindmeld-primary">MindMeld</h1>

            {step === 'register' && (
              <>
                <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
                  Create your account
                </h2>
                {validTier && (
                  <div className="mt-3 flex justify-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {TIER_LABELS[tier]} — {TIER_PRICES[tier]}
                    </span>
                  </div>
                )}
              </>
            )}

            {step === 'verify' && (
              <>
                <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
                  Verify your email
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                  We sent a verification link to <strong>{email}</strong>
                </p>
                <p className="mt-1 text-center text-xs text-gray-500">
                  Click the link in your email to verify, or enter the code below.
                </p>
                <p className="mt-1 text-center text-xs text-gray-500">
                  Can't find it? Check your spam or junk folder.
                </p>
              </>
            )}

            {step === 'checkout' && (
              <>
                <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
                  {oauthComplete ? 'Continue with your selected plan' : 'Setting up your account'}
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                  {oauthComplete ? `${TIER_LABELS[tier]} — ${TIER_PRICES[tier]}` : 'Preparing your checkout...'}
                </p>
              </>
            )}
          </div>

          {/* Step 1: Registration Form */}
          {step === 'register' && (
            <form className="mt-8 space-y-6" onSubmit={handleRegister}>
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">
                    {error}
                    {error.includes('already exists') && (
                      <Link to="/signin" className="ml-1 font-medium underline hover:text-red-800">
                        Sign in
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {/* Google OAuth */}
              <div>
                <button
                  type="button"
                  onClick={() => { startOAuth(COGNITO_CONFIG.googleEnabled ? 'Google' : undefined, tier).catch(() => setError('Could not start sign-in. Use the configured application address.')); }}
                  className="w-full flex items-center justify-center gap-3 py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mindmeld-primary"
                >
                  {COGNITO_CONFIG.googleEnabled && <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>}
                  {COGNITO_CONFIG.googleEnabled ? 'Continue with Google' : 'Continue with hosted sign-in'}
                </button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-100 text-gray-500">or</span>
                </div>
              </div>

              {/* Honeypot field — invisible to real users, bots auto-fill it */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                <label htmlFor="website">Website</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  autoComplete="off"
                  tabIndex={-1}
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              <div className="rounded-md shadow-sm space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-mindmeld-primary focus:border-mindmeld-primary sm:text-sm"
                    placeholder="you@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-mindmeld-primary focus:border-mindmeld-primary sm:text-sm"
                    placeholder="Password"
                  />
                  {password.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {[
                        { key: 'length', label: '8+ characters' },
                        { key: 'uppercase', label: 'Uppercase letter' },
                        { key: 'lowercase', label: 'Lowercase letter' },
                        { key: 'number', label: 'Number' },
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center text-xs">
                          <span className={passwordChecks[key as keyof typeof passwordChecks] ? 'text-green-600' : 'text-gray-400'}>
                            {passwordChecks[key as keyof typeof passwordChecks] ? '\u2713' : '\u2022'} {label}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-mindmeld-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mindmeld-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating account...
                    </span>
                  ) : (
                    'Create account'
                  )}
                </button>
              </div>

              <div className="text-center text-sm space-y-2">
                <p className="text-gray-600">
                  Already have an account?{' '}
                  <Link to="/signin" className="text-mindmeld-primary hover:text-blue-700 font-medium">
                    Sign in
                  </Link>
                </p>
                <a href="https://mindmeld.dev" className="text-mindmeld-primary hover:text-blue-700 block">
                  Back to homepage
                </a>
              </div>
            </form>
          )}

          {/* Step 2: Email Verification */}
          {step === 'verify' && (
            <form className="mt-8 space-y-6" onSubmit={handleVerify}>
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              )}

              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                  Verification code
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-mindmeld-primary focus:border-mindmeld-primary text-center text-2xl tracking-widest sm:text-2xl"
                  placeholder="000000"
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting || verificationCode.length !== 6}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-mindmeld-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mindmeld-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </span>
                  ) : (
                    'Verify email'
                  )}
                </button>
              </div>

              <div className="text-center text-sm">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0}
                  className="text-mindmeld-primary hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {resendCooldown > 0
                    ? `Resend code in ${resendCooldown}s`
                    : "Didn't receive a code? Resend"}
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Checkout Redirect */}
          {step === 'checkout' && (
            <div className="mt-8 flex flex-col items-center space-y-4">
              {error ? (
                <>
                  <div className="rounded-md bg-red-50 p-4 w-full">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                  <Link
                    to="/signin"
                    className="text-mindmeld-primary hover:text-blue-700 font-medium"
                  >
                    Go to sign in
                  </Link>
                </>
              ) : oauthComplete && !isSubmitting ? (
                <button type="button" onClick={handleCheckout} className="rounded-md bg-mindmeld-primary px-4 py-2 text-white">Continue to checkout</button>
              ) : (
                <div className="flex items-center space-x-3">
                  <svg className="animate-spin h-8 w-8 text-mindmeld-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-gray-600">Redirecting to checkout...</span>
                </div>
              )}
            </div>
          )}

          <div className="text-center">
            <div className="text-gray-500 text-xs">
              <a href="https://mindmeld.dev/terms.html" className="hover:underline" target="_blank" rel="noopener noreferrer">Terms</a>
              {' \u00B7 '}
              <a href="https://mindmeld.dev/privacy.html" className="hover:underline" target="_blank" rel="noopener noreferrer">Privacy</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
